<script>
  import { onMount } from 'svelte';
  import { get } from 'svelte/store';
  import { navigate as goto } from 'svelte-routing';
  import Pagination from '$lib/Pagination.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faRotate, faPlus, faPencil, faTrash, faSquarePlus, faTrashCan, faHistory, faXmark, faCircleXmark } from '@fortawesome/pro-light-svg-icons';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { userRoles, userReadOnly } from '$stores/sessionStore';
  import { studyProperties } from '$stores/configStore';
  import { hasAuditLogAccess, hasAccessUiAction } from '$utils/permissionUtils.js';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '$lib/Footer.svelte';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import StudyDetails from '$lib/StudyDetails.svelte';
  import ConfirmationModal from '$lib/ConfirmationModal.svelte';
  import LanguageModal from '$lib/LanguageModal.svelte';
  import AuditLogModal from '$lib/AuditLogModal.svelte';

  // Use local API endpoints instead of direct API with key 
  const API_BASE_URL = '/api/proxy';

  let languages = [];
  let resultCount = 0;
  let currentPage = 1;
  let totalPages = 1;
  let limit = 20;
  let searchText = '';
  let sortField = 'language';
  let sortOrder = 'asc';
  
  // Modal state
  let showCreateModal = false;
  let showEditModal = false;
  let showDeleteModal = false;
  let showAuditLog = false;
  let selectedLanguage = null;
  let auditLogLanguage = null;
  let isLoading = false;

  onMount(async () => {
    selectedSite.set(null);
    selectedParticipant.set(null);
    await fetchLanguages(1);
  });

  async function fetchLanguages(page = 1) {
    try {
      isLoading = true;
      let url = `${API_BASE_URL}/languages?page=${page}&pageSize=${limit}&sortField=${sortField}&sortOrder=${sortOrder}`;

      if (searchText) {
        url += `&searchText=${encodeURIComponent(searchText)}`;
      }

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch languages');
      }

      const responseData = await res.json();

      resultCount = responseData.totalCount || 0;
      currentPage = responseData.page || 1;
      totalPages = responseData.totalPages || 1;
      languages = responseData.data;
    } catch (err) {
      console.error('Error fetching languages:', err);
      languages = [];
    } finally {
      isLoading = false;
    }
  }

  function handleRefresh() {
    fetchLanguages(currentPage);
  }

  function handleClearSearch() {
    searchText = '';
    fetchLanguages(1);
  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      fetchLanguages(newPage);
    }
  }

  function handleSort(field) {
    if (sortField === field) {
      sortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      sortField = field;
      sortOrder = 'asc';
    }
    fetchLanguages(1);
  }
  
  function openCreateModal() {
    selectedLanguage = {
      language: '',
      country: '',
      flag: '',
      displayBase: '',
      displayLocalized: '',
      rightToLeft: false,
      region: '',
      code: ''
    };
    showCreateModal = true;
  }
  
  function openEditModal(language) {
    selectedLanguage = { ...language };
    showEditModal = true;
  }
  
  function openDeleteModal(language) {
    selectedLanguage = language;
    showDeleteModal = true;
  }
  
  function handleAuditLogClick(language) {
    auditLogLanguage = language;
    showAuditLog = true;
  }
  
  async function handleCreateLanguage(language) {
    try {
      // Check permission before creating
      if (!hasAccessUiAction('languages.add', get(userRoles), get(studyProperties), get(userReadOnly))) {
        console.error('Insufficient permissions to create language');
        return;
      }
      
      isLoading = true;
      const res = await fetch(`${API_BASE_URL}/languages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(language),
      });
      
      if (!res.ok) {
        throw new Error('Failed to create language');
      }
      
      showCreateModal = false;
      await fetchLanguages(1);
    } catch (err) {
      console.error('Error creating language:', err);
    } finally {
      isLoading = false;
    }
  }
  
  async function handleUpdateLanguage(language) {
    try {
      // Check permission before updating
      if (!hasAccessUiAction('languages.edit', get(userRoles), get(studyProperties), get(userReadOnly))) {
        console.error('Insufficient permissions to update language');
        return;
      }
      
      isLoading = true;
      const res = await fetch(`${API_BASE_URL}/languages/${language._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(language),
      });
      
      if (!res.ok) {
        throw new Error('Failed to update language');
      }
      
      showEditModal = false;
      await fetchLanguages(currentPage);
    } catch (err) {
      console.error('Error updating language:', err);
    } finally {
      isLoading = false;
    }
  }
  
  async function handleDeleteLanguage() {
    try {
      // Check permission before deleting
      if (!hasAccessUiAction('languages.delete', get(userRoles), get(studyProperties), get(userReadOnly))) {
        console.error('Insufficient permissions to delete language');
        return;
      }
      
      isLoading = true;
      const res = await fetch(`${API_BASE_URL}/languages/${selectedLanguage._id}`, {
        method: 'DELETE'
      });
      
      if (!res.ok) {
        throw new Error('Failed to delete language');
      }
      
      showDeleteModal = false;
      await fetchLanguages(currentPage);
    } catch (err) {
      console.error('Error deleting language:', err);
    } finally {
      isLoading = false;
    }
  }
  
  // Add computed property for edit permissions
  $: canAddLanguages = hasAccessUiAction('languages.add', $userRoles, $studyProperties, $userReadOnly);
  $: canEditLanguages = hasAccessUiAction('languages.edit', $userRoles, $studyProperties, $userReadOnly);
  $: canDeleteLanguages = hasAccessUiAction('languages.delete', $userRoles, $studyProperties, $userReadOnly);
  $: canViewAuditLogs = hasAuditLogAccess($userRoles, $studyProperties, $userReadOnly);
</script>

<StudyHeader />

<FeatureInfo 
  title="Languages Management" 
  description="This page allows you to manage all languages in the system. You can search, create, edit, and delete languages. Each language entry includes details such as language name, country, code, and display settings."
  marginBottom="mb-0"
/>

<StudyDetails showOptionsMenu={false} />

<Toolbar title="Languages" itemCountTitle="Languages" itemCount={resultCount}>
  <div class="flex items-center gap-4">
    <div class="relative">
      <input
        type="text"
        placeholder="Search"
        bind:value={searchText}
        on:input={() => fetchLanguages(1)}
        class="px-3 py-1.5 text-xs text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 pr-8"
      />
      {#if searchText}
        <button
          on:click={handleClearSearch}
          class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          title="Clear search"
        >
          <FontAwesomeIcon icon={faCircleXmark} />
        </button>
      {/if}
    </div>
    <button 
      on:click={openCreateModal} 
      class="pagination mx-1 p-0" 
      disabled={!canAddLanguages}
      class:opacity-50={!canAddLanguages}
      title={!canAddLanguages ? 'Insufficient permissions' : 'Add new language'}
    >
      <FontAwesomeIcon icon={faSquarePlus} />
    </button>
    <div class="text-white dark:text-white">
      <button on:click={handleRefresh} class="pagination mx-1 p-0" disabled={isLoading}>
        <FontAwesomeIcon icon={faRotate} spin={isLoading} />
      </button>
    </div>
  </div>
</Toolbar>

<div class="px-1 pt-0">
  <table class="min-w-full border-collapse table-auto">
    <thead>
      <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
        <th class="px-4 py-2 text-left bold cursor-pointer" on:click={() => handleSort('language')}>
          Language {sortField === 'language' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
        </th>
        <th class="px-4 py-2 text-left bold cursor-pointer" on:click={() => handleSort('country')}>
          Country {sortField === 'country' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
        </th>
        <th class="px-4 py-2 text-left bold">Flag</th>
        <th class="px-4 py-2 text-left bold">Display Base</th>
        <th class="px-4 py-2 text-left bold">Display Localized</th>
        <th class="px-4 py-2 text-left bold cursor-pointer" on:click={() => handleSort('code')}>
          Code {sortField === 'code' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
        </th>
        <th class="px-4 py-2 text-left bold">Right to Left</th>
        <th class="w-px px-4 py-2 text-left bold">Actions</th>
      </tr>
    </thead>
    <tbody>
      {#each languages as language, index}
        <tr
          class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
            2 ===
          0
            ? 'bg-stone-50 dark:bg-gray-725'
            : 'bg-stone-75 dark:bg-gray-750'}"
        >
          <td class="px-4 py-2">{language.language}</td>
          <td class="px-4 py-2">{language.country}</td>
          <td class="px-4 py-2">{language.flag}</td>
          <td class="px-4 py-2">{language.displayBase}</td>
          <td class="px-4 py-2">{language.displayLocalized}</td>
          <td class="px-4 py-2">{language.code}</td>
          <td class="px-4 py-2">{language.rightToLeft ? 'Yes' : 'No'}</td>
          <td class="w-px px-4 py-2">
            <div class="flex items-center space-x-2">
              <button 
                on:click={() => openEditModal(language)} 
                class="pagination mx-1 p-0" 
                title="Edit"
                disabled={!canEditLanguages}
                class:opacity-50={!canEditLanguages}
              >
                <FontAwesomeIcon icon={faPencil} class="text-sky-800 dark:text-white/70" />
              </button>
              <button
                on:click={() => handleAuditLogClick(language)}
                class="pagination mx-1 p-0"
                title="View audit logs"
                disabled={!canViewAuditLogs}
                class:opacity-50={!canViewAuditLogs}
              >
                <FontAwesomeIcon icon={faHistory} class="text-sky-800 dark:text-white/70" />
              </button>
              <button 
                on:click={() => openDeleteModal(language)} 
                class="pagination mx-1 p-0" 
                title="Delete"
                disabled={!canDeleteLanguages}
                class:opacity-50={!canDeleteLanguages}
              >
                <FontAwesomeIcon icon={faTrashCan} class="text-sky-800 dark:text-white/70" />
              </button>
            </div>
          </td>
        </tr>
      {/each}
      {#if languages.length === 0}
        <tr>
          <td colspan="8" class="text-center text-gray-500 dark:text-slate-400 mt-10 py-10">
            No languages found
          </td>
        </tr>
      {/if}
    </tbody>
  </table>
</div>

<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
</Footer>

{#if showCreateModal}
  <LanguageModal
    language={selectedLanguage}
    onClose={() => (showCreateModal = false)}
    onSave={handleCreateLanguage}
    title="Create Language"
  />
{/if}

{#if showEditModal}
  <LanguageModal
    language={selectedLanguage}
    onClose={() => (showEditModal = false)}
    onSave={handleUpdateLanguage}
    title="Edit Language"
  />
{/if}

{#if showDeleteModal}
  <ConfirmationModal
    showConfirmation={true}
    message={`Are you sure you want to delete the language "${selectedLanguage?.language}"? This action cannot be undone.`}
    confirmPhrase="delete"
    on:confirm={handleDeleteLanguage}
    on:close={() => (showDeleteModal = false)}
  />
{/if}

{#if showAuditLog}
  <AuditLogModal
    showModal={showAuditLog}
    documentId={auditLogLanguage?._id}
    collection="Language"
    title={`Audit Logs - ${auditLogLanguage?.language || 'Language'}`}
    on:close={() => (showAuditLog = false)}
  />
{/if}
